// Período do boletim — o vocabulário de tempo do agente, compartilhado entre
// servidor e client (Rizoma_Agente_Etapa1_Boletim_Spec.md §1.1).
//
// ⚠️ Por que "período" e não "data": vai existir um boletim SEMANAL (consolidado
// da semana). Ele não é construído no Ciclo 2, mas agendamento, idempotência e
// modelo de dados nascem aqui — e os três, se assumirem "um boletim por dia",
// forçam rewrite depois. No diário `inicio === fim`; custo zero hoje.
//
// Este arquivo mora em shared/ porque o client precisa **da mesma** função de
// formatação que o servidor usa no cabeçalho: o histórico de envios em /agente
// mostra o período de cada linha, e duas implementações divergiriam no dia em que
// o semanal entrar. As funções de data civil (sem fuso) moram aqui pelo mesmo
// motivo; `server/lib/data-tz.ts` as reexporta para não quebrar nenhum import.

export type TipoBoletim = "diario" | "semanal";

export interface PeriodoBoletim {
  tipo: TipoBoletim;
  /** Primeiro dia coberto (YYYY-MM-DD), inclusive. */
  inicio: string;
  /** Último dia coberto (YYYY-MM-DD), inclusive. No diário, igual a `inicio`. */
  fim: string;
}

/** Soma (ou subtrai) dias de uma data civil YYYY-MM-DD, sem passar por fuso nenhum. */
export function somarDias(dataIso: string, dias: number): string {
  const partes = dataIso.split("-");
  const d = new Date(Date.UTC(Number(partes[0]), Number(partes[1]) - 1, Number(partes[2])));
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

const DIAS_SEMANA_ABREV = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

/** "2026-08-01" → "sex". Calculado da data civil, sem fuso (nunca `new Date(str)` local). */
export function diaSemanaAbrev(dataIso: string): string {
  const partes = dataIso.split("-");
  const d = new Date(Date.UTC(Number(partes[0]), Number(partes[1]) - 1, Number(partes[2])));
  return DIAS_SEMANA_ABREV[d.getUTCDay()];
}

/**
 * "2026-08-01" → 8. O MÊS CIVIL de uma data, fatiando a string.
 *
 * ⚠️ Existe porque o caminho "óbvio" está errado e já custou dado em produção:
 *
 *     new Date(iso + "T00:00:00Z").getMonth()   // ← instante em UTC, mês em hora LOCAL
 *
 * Num runtime a oeste de UTC (a Bahia é UTC-3), `2025-06-01T00:00:00Z` é
 * `31/05 às 21:00` local — e todo registro do dia 1º cai no MÊS ANTERIOR. Fatiar a
 * string não passa por fuso nenhum e não tem como errar.
 */
export function mesDaData(dataIso: string): number {
  return Number(dataIso.slice(5, 7));
}

/** "2026-08-01" → "2026-08". A chave de agrupamento por mês, sem passar por fuso. */
export function mesAnoDaData(dataIso: string): string {
  return dataIso.slice(0, 7);
}

/** "2026-08-01" → 2026. Mesmo motivo de `mesDaData`. */
export function anoDaData(dataIso: string): number {
  return Number(dataIso.slice(0, 4));
}

/** "2026-08-01" → "01/08" (o boletim é do período; o ano é ruído na mensagem). */
export function dataCurtaBR(dataIso: string): string {
  const partes = dataIso.split("-");
  return `${partes[2]}/${partes[1]}`;
}

/** Dia da semana ISO (1 = segunda … 7 = domingo) de uma data civil. */
export function diaSemanaIso(dataIso: string): number {
  const partes = dataIso.split("-");
  const d = new Date(Date.UTC(Number(partes[0]), Number(partes[1]) - 1, Number(partes[2])));
  return d.getUTCDay() === 0 ? 7 : d.getUTCDay();
}

/** Período de um dia só — o formato do boletim diário e da prévia sob demanda. */
export function periodoDeUmDia(data: string, tipo: TipoBoletim = "diario"): PeriodoBoletim {
  return { tipo, inicio: data, fim: data };
}

/**
 * A ÚNICA formatação de período do produto: "sex 01/08" quando é um dia só,
 * "27/07 a 02/08" quando é intervalo. Usada no cabeçalho do boletim (servidor) e
 * em cada linha do histórico de envios (client) — se cada lado formatasse do seu
 * jeito, o histórico e o email diriam coisas diferentes sobre o mesmo envio.
 */
export function formatarPeriodo(periodo: { inicio: string; fim: string }): string {
  if (periodo.inicio === periodo.fim) {
    return `${diaSemanaAbrev(periodo.inicio)} ${dataCurtaBR(periodo.inicio)}`;
  }
  return `${dataCurtaBR(periodo.inicio)} a ${dataCurtaBR(periodo.fim)}`;
}

/**
 * O trecho que vai depois de "Boletim" no cabeçalho e no assunto do email:
 * "de sex 01/08" (diário) × "da semana de 27/07 a 02/08" (semanal).
 */
export function rotuloPeriodo(periodo: PeriodoBoletim): string {
  const prefixo = periodo.tipo === "semanal" ? "da semana de" : "de";
  return `${prefixo} ${formatarPeriodo(periodo)}`;
}

/** Capacidade da assinatura ("boletim.diario") → tipo do período. */
export function tipoDaCapacidade(capacidade: string): TipoBoletim {
  return capacidade === "boletim.semanal" ? "semanal" : "diario";
}

// ─── Semana civil (boletim SEMANAL, a partir de 08/2026) ──────────────────────
//
// A semana do boletim é a semana ISO (segunda a domingo) RECORTADA em segunda a
// sexta. A fazenda não registra movimento no fim de semana: incluir sáb/dom só
// acrescentaria dois dias sempre vazios ao rótulo e faria o leitor procurar
// número que nunca vai existir. Decisão consciente do Hugo — registro feito num
// sábado fica fora de qualquer boletim, e está escrito aqui pra que ninguém
// "conserte" isso achando que é bug de intervalo.

/** Semana ISO (1-53) + ano ISO de uma data civil — a mesma conta de shared/resumo-dia.ts. */
export function semanaIso(dataIso: string): { semana: number; ano: number } {
  const partes = dataIso.split("-");
  const d = new Date(Date.UTC(Number(partes[0]), Number(partes[1]) - 1, Number(partes[2])));
  const diaNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - diaNum);
  const inicioAno = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return {
    semana: Math.ceil((((d.getTime() - inicioAno.getTime()) / 86400000) + 1) / 7),
    ano: d.getUTCFullYear(),
  };
}

/**
 * Segunda a sexta da semana ISO que CONTÉM `dataIso` — data civil pura, sem fuso.
 *
 * No agendado o semanal só dispara no sábado, então "a semana que contém hoje"
 * é exatamente a seg–sex que acabou de fechar. Na prévia da tela qualquer data
 * serve: escolher uma quarta devolve a mesma semana que escolher o sábado dela.
 * É por isso que a função é da DATA e não do instante — `resolverPeriodo` em
 * server/lib/data-tz.ts é quem traduz "agora + fuso da assinatura" numa data
 * civil antes de chamar aqui, e a prévia entra direto com a data escolhida.
 */
export function periodoSemanal(dataIso: string): PeriodoBoletim {
  const segunda = somarDias(dataIso, 1 - diaSemanaIso(dataIso));
  return { tipo: "semanal", inicio: segunda, fim: somarDias(segunda, 4) };
}

/**
 * Cabeçalho do semanal: "Semana 11–15/08 (S33)" — e "Semana 29/09–03/10 (S40)"
 * quando a semana atravessa a virada do mês (repetir o mês nos dois lados só
 * quando ele muda mantém a linha curta sem nunca ficar ambígua).
 *
 * O "S33" é o MESMO rótulo de semana ISO que a tela Início já mostra no topo —
 * quem lê o boletim e abre o app tem que ver o mesmo número.
 */
export function rotuloSemana(periodo: { inicio: string; fim: string }): string {
  const mesInicio = periodo.inicio.slice(5, 7);
  const mesFim = periodo.fim.slice(5, 7);
  const intervalo = mesInicio === mesFim
    ? `${periodo.inicio.slice(8, 10)}–${dataCurtaBR(periodo.fim)}`
    : `${dataCurtaBR(periodo.inicio)}–${dataCurtaBR(periodo.fim)}`;
  return `Semana ${intervalo} (S${semanaIso(periodo.inicio).semana})`;
}
